import {
  FlightSearchResponseItinerary,
  FlightSearchResponseLeg,
  FlightSearchResponseSegment,
} from '@shared/models/flight-search-response.model';

export class FlightFilterService {
  public static FilterItineraries(
    filters: { name: string; value: any }[],
    ids: string[] | number[],
    entities: { [id: string]: FlightSearchResponseItinerary },
    filteredLegs: { [id: string]: FlightSearchResponseLeg },
    filteredSegments: { [id: string]: FlightSearchResponseSegment }
  ): FlightSearchResponseItinerary[] {
    const appliedFilters = filters.map((filter) => filter.name);
    const filterBySegment = appliedFilters.includes('airline');
    const filterByLeg = !filterBySegment || appliedFilters.length > 1;
    const result = [];

    ids.forEach((id: string | number) => {
      const itinerary = entities[id];
      const segmentIds = [];
      let legsAreValid = true;
      let segmentsAreValid = false;

      for (const legId of itinerary.legs) {
        if (filterBySegment && legId in filteredLegs) {
          segmentIds.push(...filteredLegs[legId].segments);
        }

        if (filterByLeg && !(legId in filteredLegs)) {
          legsAreValid = false;
          break;
        }
      }

      if (!legsAreValid) {
        return;
      }

      if (filterBySegment) {
        for (const segmentId of segmentIds) {
          if (segmentId in filteredSegments) {
            segmentsAreValid = true;
            break;
          }
        }

        if (!segmentsAreValid) {
          return;
        }
      }

      result.push(itinerary);
    });

    return result;
  }

  public static FilterLegsAndSegments(
    filters: { name: string; value: any }[],
    segments: { [id: string]: FlightSearchResponseSegment },
    legs: { [id: string]: FlightSearchResponseLeg },
    ids: (string | number)[],
    entities: { [id: string]: FlightSearchResponseItinerary }
  ): {
    filteredLegs: { [id: string]: FlightSearchResponseLeg };
    filteredSegments: { [id: string]: FlightSearchResponseSegment };
  } {
    const filteredSegments = { ...segments };
    const filteredLegs = { ...legs };
    const outboundLegs = ids.reduce((result, id) => {
      const outboundLeg = entities[id].legs[0];

      if (!result.includes(outboundLeg)) {
        result.push(outboundLeg);
      }

      return result;
    }, []);

    const inboundLegs = ids.reduce((result, id) => {
      const inboundLeg = entities[id].legs[1];

      if (!result.includes(inboundLeg)) {
        result.push(inboundLeg);
      }

      return result;
    }, []);
    filters.forEach((filterObj) => {
      switch (filterObj.name) {
        case 'airline':
          for (const segmentId of Object.keys(filteredSegments)) {
            const { marketing_airline_id } = filteredSegments[segmentId];
            const filterArray: number[] = JSON.parse(filterObj.value).map((el: string) => parseInt(el, 10));
            if (filterArray.indexOf(marketing_airline_id) < 0) {
              delete filteredSegments[segmentId];
            }
          }

          break;

        case 'outbound_direct_flight_only': {
          const outboundToRemove = [];

          outboundLegs.forEach((legId, index) => {
            if (!filteredLegs[legId]) return;

            if (filteredLegs[legId].stop_count > 0) {
              outboundToRemove.push({ legId, index });
            }
          });

          outboundToRemove.forEach((remove, i) => {
            outboundLegs.splice(remove.index - i, 1);
            delete filteredLegs[remove.legId];
          });

          break;
        }

        case 'inbound_direct_flight_only': {
          const inboundToRemove = [];

          inboundLegs.forEach((legId) => {
            if (!filteredLegs[legId]) return;

            if (filteredLegs[legId].stop_count > 0) {
              inboundToRemove.push(legId);
            }
          });

          inboundToRemove.forEach((legId) => {
            delete filteredLegs[legId];
          });

          break;
        }

        case 'departure_after_local_time': {
          const toRemove = [];
          const departureBeforeFilterValue = filters.find(
            (filter) => filter.name === 'departure_before_local_time'
          ).value;

          outboundLegs.forEach((legId, index) => {
            if (!filteredLegs[legId]) return;

            const flightHour = new Date(filteredLegs[legId].start_datetime).getHours();
            const departureAfterFilterHour = parseInt(filterObj.value.substr(0, 2), 10);
            const departureBeforeFilterHour = parseInt(departureBeforeFilterValue.substr(0, 2), 10);

            if (flightHour < departureAfterFilterHour || flightHour > departureBeforeFilterHour) {
              toRemove.push({ legId, index });
            }
          });

          toRemove.forEach((remove, i) => {
            outboundLegs.splice(remove.index - i, 1);
            delete filteredLegs[remove.legId];
          });

          break;
        }

        case 'return_after_local_time': {
          const toRemove = [];
          const returnBeforeFilterValue = filters.find((filter) => filter.name === 'return_before_local_time').value;

          inboundLegs.forEach((legId, index) => {
            if (!filteredLegs[legId]) return;

            const flightHour = new Date(filteredLegs[legId].start_datetime).getHours();
            const returnAfterFilterHour = parseInt(filterObj.value.substr(0, 2), 10);
            const returnBeforeFilterHour = parseInt(returnBeforeFilterValue.substr(0, 2), 10);

            if (flightHour < returnAfterFilterHour || flightHour > returnBeforeFilterHour) {
              toRemove.push({ legId, index });
            }
          });

          toRemove.forEach((remove, i) => {
            inboundLegs.splice(remove.index - i, 1);
            delete filteredLegs[remove.legId];
          });

          break;
        }
        default:
          break;
      }
    });

    return {
      filteredLegs,
      filteredSegments,
    };
  }
}
